import { FC, useMemo } from 'react';

import { Grid, useTheme, Skeleton, Box, Typography } from '@mui/material';

import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';
import { HeadingTag, Maybe } from '@/types';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import TAGButton from '@/components/TAGButton';

import { IFacilitiesData } from '../GoogleMap';
import { RequestStatus } from '../GoogleMap.helpers';
import { IGoogleMapsSection } from '../StandardGoogleMap/GoogleMapSection/GoogleMapSection';

import CumulativeGoogleMapSection from './CumulativeGoogleMapSection';
import {
	locationsListStyles,
	locationsWrapStyles,
	getHeadingStyles,
	stateLocationsGridStyles,
	buttonStyles,
	mapAndLocationsComponentStyles,
	stateLocationsGridItemStyles,
} from './CumulativeGoogleMap.styles';
import { getStateNameByCode, groupedByStates } from './CumulativeGoogleMap.helpers';

interface ICumulativeGoogleMap {
	facilitiesData: IFacilitiesData;
	googleMapsSectionProps: IGoogleMapsSection;
	isSmallScreen: boolean;
	facilityBasePath: string;
	title?: Maybe<string>;
	titleHtag?: Maybe<HeadingTag>;
}

const LoadingSkeleton: FC<{ isSmallScreen: boolean }> = ({ isSmallScreen }) => (
	<Grid
		data-test-id="cumulative_map_skeleton_grid_container"
		container
		direction={isSmallScreen ? 'column' : 'row'}
		sx={mapAndLocationsComponentStyles}
	>
		<Grid item data-test-id="cumulative_map_skeleton_left_grid" md={4}>
			<Skeleton variant="rectangular" height={600} />
		</Grid>
		<Grid item data-test-id="cumulative_map_skeleton_right_grid" md={8}>
			<Skeleton variant="rectangular" height={600} />
		</Grid>
	</Grid>
);

const CumulativeGoogleMap: FC<ICumulativeGoogleMap> = ({
	title,
	titleHtag = 'h1',
	facilitiesData,
	googleMapsSectionProps,
	isSmallScreen,
	facilityBasePath,
}) => {
	const theme = useTheme();

	const stateGroups = useMemo(() => groupedByStates(facilitiesData.items), [facilitiesData.items]);

	if (facilitiesData.reqStatus !== RequestStatus.Success) {
		return <LoadingSkeleton isSmallScreen={isSmallScreen} />;
	}

	return (
		<Grid
			data-test-id="cumulative_map_grid_container"
			container
			direction={isSmallScreen ? 'column' : 'row'}
			sx={mapAndLocationsComponentStyles}
		>
			{/* Locations list container */}
			<Grid data-test-id="cumulative_map_locations_container" item container md={4} sx={locationsWrapStyles}>
				<Grid data-test-id="cumulative_map_title" item xs={12}>
					<Typography
						data-test-id="google_map_title"
						variant={getHeadingVariant(titleHtag as HeadingTag)}
						component={titleHtag === 'normal' ? 'p' : titleHtag === null ? 'h1' : titleHtag}
						color="primary"
						sx={{
							...getHeadingStyles(theme, 'header2'),
							margin: '20px 0',
						}}
					>
						{title}
					</Typography>
				</Grid>
				{/* State-grouped locations */}
				<Grid item xs={12} sx={locationsListStyles} data-test-id="cumulative_map_location_list">
					{stateGroups.map(({ stateCode, facilities }) => (
						<Box key={stateCode} sx={stateLocationsGridItemStyles}>
							<Typography
								variant="bodyMediumBold"
								color="primary.main"
								data-test-id="cumulative_map_state_name"
							>
								{getStateNameByCode(stateCode)}
							</Typography>
							<Box sx={stateLocationsGridStyles}>
								{facilities.map((facility) => (
									<Typography
										key={`cumulative_map_facility_name_${facility.code}`}
										variant="bodyMediumBook"
										color="primary.main"
										data-test-id={`cumulative_map_facility_name_${facility.code}`}
									>
										{facility.name}
									</Typography>
								))}
							</Box>
						</Box>
					))}
				</Grid>
				<Grid data-test-id="cumulative_map_title" item xs={12}>
					<TAGButton
						size="XS"
						href={facilityBasePath}
						variant="tertiaryDefault"
						endIcon={<TAGSvgIcon icon="East" size={20} />}
						sx={buttonStyles}
						dataTestId="cumulative_map_see_all_locations_button"
					>
						See All Locations
					</TAGButton>
				</Grid>
			</Grid>

			{/* Map section */}
			<CumulativeGoogleMapSection {...googleMapsSectionProps} />
		</Grid>
	);
};

export default CumulativeGoogleMap;
